import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
	RegistrationServiceConfigDtoInput,
	ServicesProvider,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to create or update registration service configuration for a services provider
 */
export const createRegistrationService = async (
	servicesProviderId: string,
	data: RegistrationServiceConfigDtoInput,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.registration(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for creating or updating registration service configuration for a services provider
 */
export const useUpdateRegistrationService = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: RegistrationServiceConfigDtoInput) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to create registration service without a services provider ID.',
				);
			}
			return createRegistrationService(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(
				queries.servicesProviders.detail(data.id).queryKey,
				data,
			);
		},
	});
};
